import { motion } from "framer-motion";
import { PhoneCall, Bot, Clock, CalendarCheck, RefreshCcw, Send, Globe, AlertTriangle } from "lucide-react";

const RECEPTION = [
  { icon: Clock, text: "Answers during busy & after hours" },
  { icon: PhoneCall, text: "Jumps in if the team can't pick up in 3 rings" },
  { icon: CalendarCheck, text: "Books, reschedules & cancels appointments" },
];

const EMPLOYEE = [
  { icon: Send, text: "Replies on email, SMS & every social channel" },
  { icon: Globe, text: "Works your website & chat 24/7" },
  { icon: RefreshCcw, text: "Drives re-activations & win-backs automatically" },
];

const Card = ({ tag, tagColor, title, desc, items, icon: Icon, accent, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    className="relative overflow-hidden rounded-2xl border border-border bg-card p-8 sm:p-10"
  >
    <div className={`absolute -right-16 -top-16 h-48 w-48 rounded-full ${accent} blur-3xl opacity-60`} />
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${tagColor}`}>
      <Icon className="h-3.5 w-3.5" /> {tag}
    </span>
    <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">{title}</h3>
    <p className="mt-3 text-base leading-relaxed text-muted-foreground">{desc}</p>
    <ul className="mt-6 space-y-4">
      {items.map((it) => (
        <li key={it.text} className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-secondary text-foreground">
            <it.icon className="h-4 w-4" />
          </span>
          <span className="text-sm font-medium leading-relaxed">{it.text}</span>
        </li>
      ))}
    </ul>
  </motion.div>
);

export const AIModules = () => (
  <section id="ai-modules" data-testid="ai-modules-section" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-3xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-500">
          Your AI workforce
        </p>
        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
          An AI Receptionist & Copilot that <span className="text-gradient">never clock out</span>
        </h2>
        <p className="mt-5 text-lg text-muted-foreground">
          Zero missed leads. Zero wait time. A full virtual team working alongside your front desk.
        </p>
      </div>

      <div className="mt-14 grid gap-6 lg:grid-cols-2 lg:gap-8">
        <Card
          tag="AI RECEPTIONIST"
          tagColor="bg-brand-operations/15 text-brand-operations"
          icon={PhoneCall}
          accent="bg-sky-500/20"
          title="Your front desk that never sleeps"
          desc="Picks up calls during busy hours and after hours. If your team can't answer within 3 rings, the AI Receptionist steps in to book, reschedule, cancel, or answer general questions."
          items={RECEPTION}
          delay={0}
        />
        <Card
          tag="COPILOT"
          tagColor="bg-brand-insights/15 text-brand-insights"
          icon={Bot}
          accent="bg-fuchsia-500/20"
          title="Your Copilot across every channel"
          desc="Copilot proactively works conversations on email, SMS and social — missed-SLA follow-ups, after-hours replies, reactivations and win-backs — and flags where the team is slacking before patients fall through the cracks."
          items={EMPLOYEE}
          delay={0.1}
        />
      </div>

      <div className="mt-8 grid grid-cols-3 gap-px overflow-hidden rounded-2xl border border-border bg-border">
        {[
          { v: "15+ hrs", l: "Saved per week" },
          { v: "0s", l: "Patient wait time" },
          { v: "Zero", l: "Missed leads" },
        ].map((s) => (
          <div key={s.l} className="bg-card p-6 text-center" data-testid={`ai-stat-${s.l}`}>
            <div className="font-display text-2xl font-black text-gradient sm:text-3xl">{s.v}</div>
            <div className="mt-1 text-xs font-medium text-muted-foreground">{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);
